<template>
  <div>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              Payroll Documents
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search document"
                single-line
                hide-details
              ></v-text-field>
            </v-card-title>
            <v-card-text>
              <v-container fluid>
                <v-row>
                  <v-col>
                    <h4>Salary Documents</h4>
                    <v-divider></v-divider>
                    <v-data-table
                      fixed-header
                      :headers="salaryDocumentsHeader"
                      :items="salaryDocuments"
                      :items-per-page="-1"
                      hide-default-footer
                      height="30vh"
                      :search="search"
                      :options.sync="salaryTableOptions"
                    >
                      <template v-slot:body>
                        <draggable
                          :list="salaryDocuments"
                          tag="tbody"
                          handle=".drag-handle"
                          group="salaryDocuments"
                          @end="saveOrder('salary')"
                          ghost-class="sortable-ghost"
                          drag-class="sortable-drag"
                        >
                          <tr v-for="item in salaryDocuments" :key="item.Salary_Date" class="draggable-row">
                            <td>
                              <v-icon class="drag-handle" style="cursor: move;">mdi-drag-vertical</v-icon>
                            </td>
                            <td class="px-2 text-no-wrap">{{ item.Salary_Date | dateFilter }}</td>
                            <td class="px-2 text-no-wrap">{{ item.Amount }}</td>
                            <td class="px-2 text-no-wrap">{{ item.Notes }}</td>
                            <td class="px-2 text-no-wrap" align="center">
                              <v-btn icon small color="success" @click.prevent="copyCurrentSalaryPattern(item)">
                                <v-icon>mdi-file-document</v-icon>
                              </v-btn>
                              <v-btn class="mx-3" icon small color="primary" @click.prevent="editSalaryRow(item)">
                                <v-icon>mdi-pencil-circle</v-icon>
                              </v-btn>
                              <v-btn icon small color="red darken-2" @click.prevent="deleteSalaryRow(item)">
                                <v-icon>mdi-delete-circle</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </draggable>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
                <v-row class="mt-5">
                  <v-spacer></v-spacer>
                  <v-btn small color="success" @click.prevent="createNewSalaryPattern">
                    Create Salary Pattern
                  </v-btn>
                </v-row>

                <v-row>
                  <v-col>
                    <h4>Payment Documents</h4>
                    <v-divider></v-divider>
                    <v-data-table
                      fixed-header
                      :headers="paymentDocumentsHeader"
                      :items="paymentDocuments"
                      :items-per-page="-1"
                      hide-default-footer
                      :search="search"
                      height="30vh"
                      :options.sync="paymentTableOptions"
                    >
                      <template v-slot:body>
                        <draggable
                          :list="paymentDocuments"
                          tag="tbody"
                          handle=".drag-handle"
                          group="paymentDocuments"
                          @end="saveOrder('payment')"
                          ghost-class="sortable-ghost"
                          drag-class="sortable-drag"
                        >
                          <tr v-for="item in paymentDocuments" :key="item.Payment_Date" class="draggable-row">
                            <td>
                              <v-icon class="drag-handle" style="cursor: move;">mdi-drag-vertical</v-icon>
                            </td>
                            <td class="px-2 text-no-wrap">{{ item.Payment_Date | dateFilter }}</td>
                            <td class="px-2 text-no-wrap">{{ item.Amount }}</td>
                            <td class="px-2 text-no-wrap">{{ item.Notes }}</td>
                            <td class="px-2 text-no-wrap" align="center">
                              <v-btn icon small color="success" @click.prevent="copyCurrentPaymentPattern(item)">
                                <v-icon>mdi-file-document</v-icon>
                              </v-btn>
                              <v-btn class="mx-3" icon small color="primary" @click.prevent="editPaymentRow(item)">
                                <v-icon>mdi-pencil-circle</v-icon>
                              </v-btn>
                              <v-btn icon small color="red darken-2" @click.prevent="deletePaymentRow(item)">
                                <v-icon>mdi-delete-circle</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </draggable>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
                <v-row class="mt-5">
                  <v-spacer></v-spacer>
                  <v-btn small color="success" @click.prevent="createNewPaymentPattern">
                    Create Payment Pattern
                  </v-btn>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Remove salary dialog -->
    <ConfirmOrDeniedDialog
      v-if="removeSalariesDialog"
      v-model="removeSalariesDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="'Do you want to delete current salary rows?'"
      @onDeleteClick="removeSalariesByDate"
      @onCancelClick="removeSalariesDialog = false"
      @onCloseClick="removeSalariesDialog = false"
    />
    <!-- /Remove salary dialog -->
    <!-- Remove payment dialog -->
    <ConfirmOrDeniedDialog
      v-if="removePaymentsDialog"
      v-model="removePaymentsDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="'Do you want to delete current payment rows?'"
      @onDeleteClick="removePaymentsByDate"
      @onCancelClick="removePaymentsDialog = false"
      @onCloseClick="removePaymentsDialog = false"
    />
    <!-- /Remove payment dialog -->
    <!-- Set salaries dialog -->
    <v-dialog
      v-model="salariesEditorDialog"
      max-width="95vw"
    >
      <v-card>
        <v-card-title>
          Salaries for Employee
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="salariesEditorDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row class="mb-1">
            <v-col cols="12" md="2" lg="2">
              <DatePicker
                v-model="salariesEditorDate"
              />
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="4" lg="4">
              <v-text-field
                v-model="searchForSalaries"
                append-icon="mdi-magnify"
                label="Search employee"
                single-line
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
          <v-data-table
            fixed-header
            dense
            :headers="salaryTableHeaders"
            :items="currentSalaries"
            :items-per-page="-1"
            hide-default-footer
            :search="searchForSalaries"
            item-key="Salary_ID"
            mobile-breakpoint="0"
            height="57vh"
            class="fixed-colum"
          >
            <template v-slot:[`header.Balance`]="{ header }">
              {{ header.text }}<br>
              (total: {{ totalBalanceInSalaries }})
            </template>
            <template v-slot:[`item.Full_Name`]="{ item }">
              <v-autocomplete
                dense
                hide-details
                class="mb-2"
                :items="employee"
                item-value="employee_id"
                item-text="full_name"
                v-model="item.Employee_ID"
              >
              </v-autocomplete>
            </template>
            <template v-slot:[`item.Salary`]="{ item }">
              <v-text-field
                v-model="item.Salary"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Reporting`]="{ item }">
              <v-text-field
                v-model="item.Reporting"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Premium`]="{ item }">
              <v-text-field
                v-model="item.Premium"
              ></v-text-field>
            </template>
            <template v-slot:[`item.L_And_D`]="{ item }">
              <v-text-field
                v-model="item.L_And_D"
              ></v-text-field>
            </template>
            <template v-slot:[`item.PM`]="{ item }">
              <v-text-field
                v-model="item.PM"
              ></v-text-field>
            </template>
            <template v-slot:[`item.SBU`]="{ item }">
              <v-text-field
                v-model="item.SBU"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Dividend`]="{ item }">
              <v-text-field
                v-model="item.Dividend"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Tax`]="{ item }">
              <v-text-field
                v-model="item.Tax"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Penalty`]="{ item }">
              <v-text-field
                v-model="item.Penalty"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Medical_Insurance`]="{ item }">
              <v-text-field
                v-model="item.Medical_Insurance"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Sport_Compensation`]="{ item }">
              <v-text-field
                v-model="item.Sport_Compensation"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Other_Bonuses`]="{ item }">
              <v-text-field
                v-model="item.Other_Bonuses"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Benefits`]="{ item }">
              <v-text-field
                v-model="item.Benefits"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Refund`]="{ item }">
              <v-text-field
                v-model="item.Refund"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Notes`]="{ item }">
              <v-text-field
                v-model="item.Notes"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Actions`]="{ item, index }">
              <v-btn
                icon
                small
                color="red darken-2"
                @click.prevent="deleteEmployeeSalaryRow(index, item)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-text-field
            placeholder="Notes"
            v-model="salaryNotes"
          ></v-text-field>
          <v-spacer></v-spacer>
          <v-btn small color="success" @click.prevent="addNewEmployeeToSalary">Add New</v-btn>
          <v-btn small color="primary" @click.prevent="setSalariesToEmployee">Set Salaries</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Set payment dialog -->
    <v-dialog
      v-model="paymentsEditorDialog"
      max-width="70vw"
    >
      <v-card>
        <v-card-title>
          Payments for Employee
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="paymentsEditorDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row class="mb-1">
            <v-col cols="12" md="2" lg="2">
              <DatePicker
                v-model="paymentsEditorDate"
              />
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="4" lg="4">
              <v-text-field
                v-model="searchForPayments"
                append-icon="mdi-magnify"
                label="Search employee"
                single-line
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
          <v-data-table
            fixed-header
            dense
            :headers="paymentsForEmployeeHeaders"
            :items="currentPayments"
            :items-per-page="-1"
            hide-default-footer
            :search="searchForPayments"
            item-key="Payment_ID"
            height="57vh"
          >
            <template v-slot:[`header.Balance`]="{ header }">
              {{ header.text }}<br>
              (total: {{ totalBalanceInPayments }})
            </template>
            <template v-slot:[`header.Amount`]="{ header }">
              {{ header.text }}<br>
              (total: {{ totalPayments }})
            </template>
            <template v-slot:[`item.Full_Name`]="{ item }">
              <v-select
                dense
                hide-details
                class="mb-2"
                :items="employee"
                item-value="employee_id"
                item-text="full_name"
                v-model="item.Employee_ID"
              ></v-select>
            </template>
            <template v-slot:[`item.Amount`]="{ item }">
              <v-text-field
                v-model="item.Amount"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Exchange_Rate`]="{ item }">
              <v-text-field
                v-model="item.Exchange_Rate"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Payment_UAH`]="{ item }">
              <v-text-field
                v-model="item.Payment_UAH"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Payment_Netto_UAH`]="{ item }">
              <v-text-field
                v-model="item.Payment_Netto_UAH"
              ></v-text-field>
            </template>
            <template v-slot:[`item.From`]="{ item }">
              <v-text-field
                v-model="item.From"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Notes`]="{ item }">
              <v-text-field
                v-model="item.Notes"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Actions`]="{ item, index }">
              <v-btn
                icon
                small
                color="red darken-2"
                @click.prevent="deleteEmployeePaymentRow(index, item)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-text-field
            placeholder="Notes"
            v-model="paymentNotes"
          ></v-text-field>
          <v-spacer></v-spacer>
          <v-btn small color="success" @click.prevent="addNewEmployeeToPayments">Add New</v-btn>
          <v-btn small color="primary" @click.prevent="setPaymentsToEmployee">Set Payments</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'
export default {
  name: 'PayrollDocuments',
  components: {
    draggable: () => import('vuedraggable'),
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  data () {
    return {
      search: '',
      searchEmployee: '',
      rolesOfCurrentUser: [],
      salaryDocuments: [],
      paymentDocuments: [],
      salaryDocumentDialog: false,
      paymentDocumentDialog: false,
      currentSalaries: [],
      currentPayments: [],
      salaryTableOptions: {},
      paymentTableOptions: {},
      salariesEditMode: false,
      paymentsEditMode: false,
      salariesEditorDate: formatLocalDate(new Date()),
      searchForSalaries: '',
      paymentsEditorDate: formatLocalDate(new Date()),
      searchForPayments: '',
      salariesEditorDialog: false,
      paymentsEditorDialog: false,
      salaryNotes: '',
      paymentNotes: '',
      salariesDateForRemoving: null,
      paymentsDateForRemoving: null,
      removeSalariesDialog: false,
      removePaymentsDialog: false,
      salaryDocumentsHeader: [
        { text: '', value: 'drag', sortable: false, width: '30px' },
        {
          text: 'Date of Salary',
          value: 'Salary_Date',
          divider: true,
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Amount',
          value: 'Amount',
          divider: true,
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Notes',
          value: 'Notes',
          divider: true,
          class: 'px-2 text-no-wrap'
        },
        {
          text: '#',
          align: 'center',
          sortable: false,
          value: 'Actions',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: '20%'
        }
      ],
      paymentDocumentsHeader: [
        { text: '', value: 'drag', sortable: false, width: '30px' },
        {
          text: 'Date of Payment',
          value: 'Payment_Date',
          divider: true,
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Amount',
          value: 'Amount',
          divider: true,
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Notes',
          value: 'Notes',
          divider: true,
          class: 'px-2 text-no-wrap'
        },
        {
          text: '#',
          align: 'center',
          sortable: false,
          value: 'Actions',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: '20%'
        }
      ],
      salaryTableHeaders: [
        {
          text: '№',
          value: 'RowNumber',
          class: 'text-no-wrap',
          cellClass: 'text-no-wrap',
          divider: true
        },
        {
          text: 'Name',
          value: 'Full_Name',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true,
          width: '200px'
        },
        {
          text: 'Salary',
          value: 'Salary',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true,
          width: '8%'
        },
        {
          text: 'Reporting',
          value: 'Reporting',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Premium',
          value: 'Premium',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Other Bonuses',
          value: 'Other_Bonuses',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Benefits',
          value: 'Benefits',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Refund',
          value: 'Refund',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Sport Compensation',
          value: 'Sport_Compensation',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Medical Insurance',
          value: 'Medical_Insurance',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'L and D',
          value: 'L_And_D',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'PM',
          value: 'PM',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true,
          width: '80px'
        },
        {
          text: 'SBU',
          value: 'SBU',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true,
          width: '80px'
        },
        {
          text: 'Dividend',
          value: 'Dividend',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Tax',
          value: 'Tax',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Penalty',
          value: 'Penalty',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Notes',
          value: 'Notes',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true,
          width: '15%'
        },
        {
          text: 'Balance',
          value: 'Balance',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Total',
          value: 'Total',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: '#',
          align: 'center',
          value: 'Actions',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        }
      ],
      paymentsForEmployeeHeaders: [
        {
          text: '№',
          value: 'RowNumber',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Employee',
          value: 'Full_Name',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true,
          width: '30%'
        },
        {
          text: 'Amount',
          value: 'Amount',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Exchange Rate',
          value: 'Exchange_Rate',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Payment, UAH',
          value: 'Payment_UAH',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Payment Netto, UAH',
          value: 'Payment_Netto_UAH',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'From',
          value: 'From',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Notes',
          value: 'Notes',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: 'Balance',
          value: 'Balance',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true
        },
        {
          text: '#',
          align: 'center',
          value: 'Actions',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        }
      ]
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    employee () {
      const r = new RegExp(this.searchEmployee, 'gi')
      return this.$store.getters.getEmployee.filter(item => {
        r.lastIndex = 0
        return r.test(item.full_name)
      })
    },
    isFinancier () {
      let res = false
      for (let i = 0; i < this.rolesOfCurrentUser.length; i++) {
        if (this.rolesOfCurrentUser[i].RoleName === 'financier') {
          res = true
        }
      }
      return res
    },
    allEmployee () {
      return this.$store.getters.getAllEmployee.filter(item => !item.Is_Deleted)
    },
    totalSalary () {
      let res = 0
      for (let i = 0; i < this.currentSalaries.length; i++) {
        if (this.currentSalaries[i].Salary !== 0 || this.currentSalaries[i].Reporting || this.currentSalaries[i].Premium !== 0 || this.currentSalaries[i].Sport_Compensation !== 0 || this.currentSalaries[i].Medical_Insurance !== 0 || this.currentSalaries[i].L_And_D !== 0 || this.currentSalaries[i].Other_Bonuses !== 0 || this.currentSalaries[i].Benefits !== 0 || this.currentSalaries[i].Refund !== 0 || this.currentSalaries[i].PM !== 0 || this.currentSalaries[i].SBU !== 0 || this.currentSalaries[i].Dividend !== 0 || this.currentSalaries[i].Penalty !== 0) {
          res += +this.currentSalaries[i].Salary + +this.currentSalaries[i].Reporting + +this.currentSalaries[i].Premium + +this.currentSalaries[i].Sport_Compensation + +this.currentSalaries[i].Medical_Insurance + +this.currentSalaries[i].L_And_D + +this.currentSalaries[i].Other_Bonuses + +this.currentSalaries[i].Benefits + +this.currentSalaries[i].Refund + +this.currentSalaries[i].PM + +this.currentSalaries[i].SBU + +this.currentSalaries[i].Dividend - +this.currentSalaries[i].Penalty
        }
      }
      return res
    },
    totalBalanceInSalaries () {
      let res = 0
      for (let i = 0; i < this.currentSalaries.length; i++) {
        if (this.currentSalaries[i].Balance !== 0 && this.currentSalaries[i].Balance) {
          res += Number(this.currentSalaries[i].Balance)
        }
      }
      return res
    },
    totalPayments () {
      let res = 0
      for (let i = 0; i < this.currentPayments.length; i++) {
        if (this.currentPayments[i].Amount !== 0 && this.currentPayments[i].Amount) {
          res += Number(this.currentPayments[i].Amount)
        }
      }
      return res
    },
    totalBalanceInPayments () {
      let res = 0
      for (let i = 0; i < this.currentPayments.length; i++) {
        if (this.currentPayments[i].Balance !== 0 && this.currentPayments[i].Balance) {
          res += Number(this.currentPayments[i].Balance)
        }
      }
      return res
    }
  },
  async mounted () {
    this.getEmployee()
    this.getEmployeeList()
    this.getRolesOfCurrentUser()
    await this.getSalaryDocuments()
    await this.getPaymentDocuments()
  },
  methods: {
    getEmployee () {
      this.axios.get('/api/employee', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.setEmployeeList(0, res.data)
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getEmployeeList () {
      this.axios.get('/api/employeelist', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setEmployeeList', { employeeList: response.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    setEmployeeList (beginNumber, employee) {
      const empl = []
      for (let i = beginNumber; i < beginNumber + 100; i++) {
        if (employee[i]) {
          empl.push(employee[i])
        } else {
          break
        }
      }

      if (empl.length) {
        this.$store.dispatch('addEmployee', { employee: empl })
      }

      if (empl.length === 100) {
        setTimeout(() => {
          this.setEmployeeList(beginNumber + 100, employee)
        })
      }
    },
    getRolesOfCurrentUser () {
      this.axios.get('/api/employee_role/' + this.user.login, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.rolesOfCurrentUser = result.data
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    async getSalaryDocuments () {
      try {
        const res = await this.axios.get('/api/payrollDocuments/salaries', {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.salaryDocuments = res.data
        this.applySavedOrder('salary')
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },

    async getPaymentDocuments () {
      try {
        const res = await this.axios.get('/api/payrollDocuments/payments', {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.paymentDocuments = res.data
        this.applySavedOrder('payment')
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    createNewSalaryPattern () {
      this.currentSalaries = []
      this.salariesEditorDate = formatLocalDate(new Date())
      this.salaryNotes = ''
      this.salariesEditMode = false
      this.salariesEditorDialog = true
    },
    createNewPaymentPattern () {
      this.currentPayments = []
      this.paymentsEditorDate = formatLocalDate(new Date())
      this.paymentNotes = ''
      this.paymentsEditMode = false
      this.paymentsEditorDialog = true
    },
    copyCurrentSalaryPattern (item) {
      this.getSalariesByDate(item)
      this.salariesEditorDate = formatLocalDate(item.Salary_Date)
      this.salaryNotes = item.Notes
      this.salariesEditMode = false
      this.salariesEditorDialog = true
    },
    editSalaryRow (item) {
      this.getSalariesByDate(item)
      this.salariesEditMode = true
      this.salariesEditorDate = formatLocalDate(item.Salary_Date)
      this.salaryNotes = item.Notes
      this.salariesEditorDialog = true
    },
    getSalariesByDate (item) {
      const date = formatLocalDate(item.Salary_Date)
      this.axios.get(`/api/payrollDocuments/salaries/${date}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.currentSalaries = []
        for (let i = 0; i < res.data.length; i++) {
          res.data[i].Total = res.data[i].Salary + res.data[i].Reporting + res.data[i].Premium + res.data[i].Sport_Compensation + res.data[i].Medical_Insurance + res.data[i].L_And_D + res.data[i].Other_Bonuses + res.data[i].Benefits + res.data[i].Refund + res.data[i].PM + res.data[i].SBU + res.data[i].Dividend - res.data[i].Penalty
          this.currentSalaries.push(res.data[i])
        }
        this.salaryDocumentDialog = true
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteSalaryRow (item) {
      this.salariesDateForRemoving = formatLocalDate(item.Salary_Date)
      this.removeSalariesDialog = true
    },
    removeSalariesByDate () {
      this.axios.delete(`/api/payrollDocuments/salaries/document/${this.salariesDateForRemoving}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getSalaryDocuments()
        this.salariesDateForRemoving = null
        this.removeSalariesDialog = false
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    copyCurrentPaymentPattern (item) {
      this.getPaymentsByDate(item)
      this.paymentsEditorDate = formatLocalDate(item.Payment_Date)
      this.paymentNotes = item.Notes
      this.paymentsEditMode = false
      this.paymentsEditorDialog = true
    },
    editPaymentRow (item) {
      this.getPaymentsByDate(item)
      this.paymentsEditMode = true
      this.paymentsEditorDate = formatLocalDate(item.Payment_Date)
      this.paymentNotes = item.Notes
      this.paymentsEditorDialog = true
    },
    getPaymentsByDate (item) {
      const date = formatLocalDate(item.Payment_Date)
      this.axios.get(`/api/payrollDocuments/payments/${date}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.currentPayments = []
        for (let i = 0; i < res.data.length; i++) {
          this.currentPayments.push(res.data[i])
        }
        this.paymentDocumentDialog = true
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deletePaymentRow (item) {
      this.paymentsDateForRemoving = formatLocalDate(item.Payment_Date)
      this.removePaymentsDialog = true
    },
    removePaymentsByDate () {
      this.axios.delete(`/api/payrollDocuments/payments/document/${this.paymentsDateForRemoving}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getPaymentDocuments()
        this.paymentsDateForRemoving = null
        this.removePaymentsDialog = false
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    setSalariesToEmployee () {
      const body = {
        Salaries: this.currentSalaries,
        Salary_Date: formatLocalDate(this.salariesEditorDate),
        Total: this.totalSalary,
        Notes: this.salaryNotes
      }
      if (this.salariesEditMode) {
        this.axios.put(`/api/payrollDocuments/salaries/${body.Salary_Date}`, body, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.getSalaryDocuments()
          this.salariesEditorDialog = false
          this.salaryNotes = ''
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      } else {
        this.axios.post('/api/payrollDocuments/salaries/', body, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.getSalaryDocuments()
          this.salariesEditorDialog = false
          this.salaryNotes = ''
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    setPaymentsToEmployee () {
      const body = {
        Payments: this.currentPayments,
        Payment_Date: formatLocalDate(this.paymentsEditorDate),
        Amount: this.totalPayments,
        Notes: this.paymentNotes
      }
      if (this.paymentsEditMode) {
        this.axios.put(`/api/payrollDocuments/payments/${body.Payment_Date}`, body, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.getPaymentDocuments()
          this.paymentsEditorDialog = false
          this.paymentNotes = ''
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      } else {
        this.axios.post('/api/payrollDocuments/payments', body, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.getPaymentDocuments()
          this.paymentsEditorDialog = false
          this.paymentNotes = ''
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    addNewEmployeeToSalary () {
      const salary = {
        Salary_ID: null,
        RowNumber: this.currentSalaries.length + 1,
        Salary: 0,
        Reporting: 0,
        Premium: 0,
        L_And_D: 0,
        PM: 0,
        SBU: 0,
        Dividend: 0,
        Penalty: 0,
        Medical_Insurance: 0,
        Sport_Compensation: 0,
        Other_Bonuses: 0,
        Benefits: 0,
        Refund: 0,
        Tax: 0,
        Notes: ''
      }
      this.currentSalaries.push(salary)
    },
    addNewEmployeeToPayments () {
      const payment = {
        Payment_ID: null,
        RowNumber: this.currentPayments.length + 1,
        Amount: 0,
        Exchange_Rate: 0,
        Payment_UAH: 0,
        Payment_Netto_UAH: 0,
        From: '',
        Notes: ''
      }
      this.currentPayments.push(payment)
    },
    deleteEmployeeSalaryRow (index, item) {
      if (this.salariesEditMode) {
        if (item.Salary_ID) {
          this.axios.delete(`/api/payrollDocuments/salaries/${item.Salary_ID}`, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          }).then(res => {
            this.currentSalaries.splice(index, 1)
            this.getSalaryDocuments()
          }).catch(error => {
            const errorMsg = getApiErrorMessage(error)
            this.$store.dispatch('setError', { errorMsg })
          })
        } else {
          this.currentSalaries.splice(index, 1)
        }
      } else {
        this.currentSalaries.splice(index, 1)
      }
    },
    deleteEmployeePaymentRow (index, item) {
      if (this.paymentsEditMode) {
        if (item.Payment_ID) {
          this.axios.delete(`/api/payrollDocuments/payments/${item.Payment_ID}`, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          }).then(res => {
            this.currentPayments.splice(index, 1)
            this.getPaymentDocuments()
          }).catch(error => {
            const errorMsg = getApiErrorMessage(error)
            this.$store.dispatch('setError', { errorMsg })
          })
        } else {
          this.currentPayments.splice(index, 1)
        }
      } else {
        this.currentPayments.splice(index, 1)
      }
    },
    getOrderConfig (type) {
      if (type === 'salary') {
        return {
          items: this.salaryDocuments,
          storageKey: 'salaryDocumentsOrder',
          idKey: 'Salary_Date',
          dateKey: 'Salary_Date'
        }
      }
      return {
        items: this.paymentDocuments,
        storageKey: 'paymentDocumentsOrder',
        idKey: 'Payment_Date',
        dateKey: 'Payment_Date'
      }
    },
    readSavedOrderState (type) {
      const config = this.getOrderConfig(type)
      try {
        const raw = JSON.parse(localStorage.getItem(config.storageKey))
        if (!raw) return null
        // Back-compat: previously we stored a plain array of ids as the custom order.
        if (Array.isArray(raw)) {
          return { mode: 'custom', customOrder: raw }
        }
        return raw
      } catch (e) {
        return null
      }
    },
    writeSavedOrderState (type, state) {
      const config = this.getOrderConfig(type)
      localStorage.setItem(config.storageKey, JSON.stringify(state))
    },
    setTableOptionsSort (type, sortBy, sortDesc) {
      if (type === 'salary') {
        this.salaryTableOptions = { ...this.salaryTableOptions, sortBy, sortDesc }
      } else {
        this.paymentTableOptions = { ...this.paymentTableOptions, sortBy, sortDesc }
      }
    },
    saveOrder (type) {
      const config = this.getOrderConfig(type)
      const order = config.items.map(item => item[config.idKey])
      const existing = this.readSavedOrderState(type) || {}

      this.writeSavedOrderState(type, {
        ...existing,
        mode: 'custom',
        customOrder: order,
        sortBy: null,
        sortDesc: null
      })

      // Drag-and-drop wins over header sort – clear the indicator to avoid a stale arrow.
      this.setTableOptionsSort(type, [], [])
    },
    applySavedOrder (type) {
      const config = this.getOrderConfig(type)
      const saved = this.readSavedOrderState(type)

      if (!saved) return

      if (saved.mode === 'sort' && saved.sortBy) {
        this.setTableOptionsSort(type, [saved.sortBy], [!!saved.sortDesc])
        return
      }

      if (saved.mode === 'custom' && Array.isArray(saved.customOrder) && saved.customOrder.length) {
        const order = saved.customOrder
        const sortedItems = [...config.items].sort((a, b) => {
          const indexA = order.indexOf(a[config.idKey])
          const indexB = order.indexOf(b[config.idKey])
          const aIsNew = indexA === -1
          const bIsNew = indexB === -1

          // New records (not part of the saved custom order) go to the top,
          // ordered by date desc among themselves – never to the bottom.
          if (aIsNew && bIsNew) {
            return new Date(b[config.dateKey]) - new Date(a[config.dateKey])
          }
          if (aIsNew) return -1
          if (bIsNew) return 1
          return indexA - indexB
        })

        if (type === 'salary') {
          this.salaryDocuments = sortedItems
        } else {
          this.paymentDocuments = sortedItems
        }
      }
    },
    handleTableOptionsChange (type, newVal, oldVal) {
      const config = this.getOrderConfig(type)
      const newSortBy = (newVal && newVal.sortBy) || []
      const newSortDesc = (newVal && newVal.sortDesc) || []
      const oldSortBy = (oldVal && oldVal.sortBy) || []

      if (!newSortBy.length) {
        // Sort cleared by the user – drop the saved sort preference,
        // but keep the custom drag order if it exists.
        if (oldSortBy.length) {
          const saved = this.readSavedOrderState(type) || {}
          if (saved.mode === 'sort') {
            this.writeSavedOrderState(type, {
              ...saved,
              mode: saved.customOrder && saved.customOrder.length ? 'custom' : null,
              sortBy: null,
              sortDesc: null
            })
          }
        }
        return
      }

      const sortKey = newSortBy[0]
      const direction = newSortDesc[0] ? -1 : 1

      const sortedItems = [...config.items].sort((a, b) => {
        const aValue = a[sortKey]
        const bValue = b[sortKey]
        if (aValue < bValue) return -1 * direction
        if (aValue > bValue) return 1 * direction
        return 0
      })

      if (type === 'salary') {
        this.salaryDocuments = sortedItems
      } else {
        this.paymentDocuments = sortedItems
      }

      const existing = this.readSavedOrderState(type) || {}
      this.writeSavedOrderState(type, {
        ...existing,
        mode: 'sort',
        sortBy: sortKey,
        sortDesc: !!newSortDesc[0]
      })
    }
  },
  filters: {
    valToFixed (val) {
      if ((Math.ceil(val * 100) % 10) !== 0) return (+val).toFixed(2)
      if ((Math.ceil(val * 100) % 100) !== 0) return (+val).toFixed(1)
      return (+val).toFixed(0)
    },
    dateFilter (val) {
      return val ? formatLocalDate(val) : ''
    }
  },
  watch: {
    salaryTableOptions: {
      handler (newVal, oldVal) {
        this.handleTableOptionsChange('salary', newVal, oldVal)
      },
      deep: true
    },
    paymentTableOptions: {
      handler (newVal, oldVal) {
        this.handleTableOptionsChange('payment', newVal, oldVal)
      },
      deep: true
    }
  }
}
</script>
<style lang="scss">
  .v-data-table .v-text-field {
    padding-top: 0;
    margin-top: 4px;
    padding-bottom: 0;
    margin-bottom: -5px;
  }

  $id-column-width: 60px;
  $breakpoint: 600px;

  @media screen and (min-width: $breakpoint) {
    .fixed-colum > .v-data-table__wrapper > table > tbody > tr > td:nth-child(-n + 2),
    .fixed-colum > .v-data-table__wrapper > table > thead > tr > th:nth-child(-n + 2) {
      width: $id-column-width;
      position: sticky;
      left: 0;
      z-index: 9998;
      background-color: #ffffff;
    }
    .theme--dark .fixed-colum > .v-data-table__wrapper > table > tbody > tr > td:nth-child(-n + 2),
    .theme--dark .fixed-colum > .v-data-table__wrapper > table > thead > tr > th:nth-child(-n + 2) {
      background-color: #1e1e1e;
    }
    .fixed-colum > .v-data-table__wrapper > table > tbody > tr > td:nth-child(2),
    .fixed-colum > .v-data-table__wrapper > table > thead > tr > th:nth-child(2) {
      left: calc($id-column-width + 3px);
    }
    .fixed-colum > .v-data-table__wrapper > table > thead > tr > th:nth-child(-n + 2) {
      z-index: 9999;
    }
  }
  @media screen and (max-width: $breakpoint){
    .fixed-colum > .v-data-table__wrapper > table > tbody > tr > td:nth-child(1),
    .fixed-colum > .v-data-table__wrapper > table > thead > tr > th:nth-child(1) {
      width: $id-column-width;
      position: sticky;
      left: 0;
      z-index: 9998;
      background-color: #ffffff;
    }
    .theme--dark .fixed-colum > .v-data-table__wrapper > table > tbody > tr > td:nth-child(1),
    .theme--dark .fixed-colum > .v-data-table__wrapper > table > thead > tr > th:nth-child(1) {
      background-color: #1e1e1e;
    }
    .fixed-colum > .v-data-table__wrapper > table > thead > tr > th:nth-child(1) {
      z-index: 9999;
    }
  }
  .fixed-colum > .v-data-table__wrapper > table > tbody > tr:hover > td {
    background-color: inherit;
  }

.sortable-drag {
  opacity: 0.8;
  background: #f0f8ff;
}

.sortable-ghost {
  border-top: 3px solid #1976d2;
}

.sortable-ghost td {
  visibility: hidden;
}

.draggable-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.draggable-row:hover .drag-handle {
  color: #212121;
}
</style>
